/**
 * ElecBid — SOPs  (/sops, /sops/:slug)
 *
 * The company's written procedures: browse by section, search, read one, and —
 * for an admin — write and edit them. Jai-Man reads the same library through
 * its look_up_sop tool, so what is written here is what it tells the crew.
 *
 * Phone first: this is opened standing at a panel. One column, large type, the
 * exact thing to type in a box.
 *
 * Everything goes through /api/sops (api/_sops.js holds the rules). The page
 * loads the whole library once, bodies included, and searches it in the browser
 * so typing gives an answer with no signal. Pictures and videos are fetched per
 * procedure because their links are signed and expire.
 *
 * A video never passes through /api/sops (a Vercel function takes 4.5 MB): the
 * endpoint hands an admin a one-off upload link and the browser sends the file
 * straight to storage, then tells the endpoint it is there.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";
import { RichText } from "../lib/richText";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1e2328", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", faint: "#5a6374",
  red: "#e5484d", yellow: "#f5d90a",
};

const MAX_UPLOAD_BYTES = 3 * 1024 * 1024; // as-is under this; redrawn smaller above it
const PICTURE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

const input = {
  width: "100%", boxSizing: "border-box", background: C.bg, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 12px", fontSize: 16,
  fontFamily: "inherit", outline: "none",
};
const label = { display: "block", fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: ".06em", textTransform: "uppercase", margin: "14px 0 6px" };
const button = (kind = "plain") => ({
  border: `1px solid ${kind === "primary" ? C.lime : kind === "danger" ? C.red : C.border}`,
  background: kind === "primary" ? C.lime : "transparent",
  color: kind === "primary" ? "#0d0f12" : kind === "danger" ? C.red : C.text,
  borderRadius: 10, padding: "10px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
});
const chip = (on) => ({
  flex: "0 0 auto", border: `1px solid ${on ? C.lime : C.border}`, background: on ? "rgba(165,206,58,0.14)" : "transparent",
  color: on ? C.lime : C.muted, borderRadius: 999, padding: "7px 13px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit",
});
const badge = (color) => ({ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color, border: `1px solid ${color}`, borderRadius: 6, padding: "2px 6px", whiteSpace: "nowrap" });

// ── Search (mirrors api/_sops.js closely enough that the page and Jai-Man find
//    the same procedure for the same words) ───────────────────────────────────
const STOP = new Set((
  "how do i use the a an to what is are of for in on and or we my me it its does can you with when should " +
  "this that about tell explain show please prompt prompts command commands sop sops procedure process"
).split(" "));
const words = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9/]+/g, " ").split(" ").map((w) => w.replace(/^\/+|\/+$/g, "")).filter(Boolean);
const tokensOf = (q) => {
  const all = words(q);
  const kept = all.filter((w) => !STOP.has(w));
  // The server shows the index when only filler words were asked. Here someone
  // is mid-word in a search box, so fall back to what they typed.
  return [...new Set(kept.length ? kept : all)];
};

// A box opened with ```read is one with no Copy button (see RichText). The word
// is a marker for the page, not part of the procedure, so search must not match
// it or show it. The same rule is in api/_sops.js.
const plainFences = (t) => String(t || "").replace(/^([ \t]*```)[ \t]*read[ \t]*(?=\r?$)/gim, "$1");

function score(a, tokens) {
  if (!tokens.length) return 0;
  const titleWords = new Set(words(a.title));
  const keys = (a.keywords || []).map((k) => words(k).join(" ")).filter(Boolean);
  const keySet = new Set(keys);
  const title = words(a.title).join(" "), section = words(a.section).join(" ");
  const summary = words(a.summary).join(" "), body = words(plainFences(a.body)).join(" ");
  let total = 0, matched = 0;
  for (const t of tokens) {
    let s = 0;
    if (keySet.has(t)) s += 8;
    if (titleWords.has(t)) s += 6;
    // A short token ("r", "jb") only counts as a whole word; as a fragment it is
    // in everything. While typing, let it also start a keyword or a title word.
    if (t.length >= 3) {
      if (!keySet.has(t) && keys.some((k) => k.includes(t))) s += 4;
      if (!titleWords.has(t) && title.includes(t)) s += 3;
      if (section.includes(t)) s += 2;
      if (summary.includes(t)) s += 3;
      if (body.includes(t)) s += 1;
    } else if (!s && (keys.some((k) => k.startsWith(t)) || [...titleWords].some((w) => w.startsWith(t)))) {
      s += 2;
    }
    if (s > 0) matched++;
    total += s;
  }
  if (!matched) return 0;
  // Fewer than half the words of a longer search, with no exact keyword or title
  // word among them, is not a match.
  if (tokens.length > 1 && matched * 2 < tokens.length && total < 6) return 0;
  if (matched === tokens.length && tokens.length > 1) total += 5;
  return total;
}

// A line of the procedure around the first word that matched, for results whose
// title and summary do not show why they matched.
function snippet(a, tokens) {
  const long = tokens.filter((t) => t.length >= 3);
  if (!long.length) return "";
  const shown = `${a.title} ${a.summary || ""}`.toLowerCase();
  if (long.every((t) => shown.includes(t))) return "";
  const plain = plainFences(a.body).replace(/```/g, " ").replace(/[#*_`>|]/g, "").replace(/\s+/g, " ");
  const at = Math.min(...long.map((t) => plain.toLowerCase().indexOf(t)).filter((i) => i >= 0), Infinity);
  if (!Number.isFinite(at)) return "";
  const from = Math.max(0, at - 40);
  return `${from > 0 ? "…" : ""}${plain.slice(from, from + 110).trim()}…`;
}

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; }
};

// "4:49" for a video length in seconds.
const fmtDuration = (seconds) => {
  const n = Math.round(Number(seconds));
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};
const fmtSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n >= 1048576 ? `${Math.round(n / 1048576)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

// ── Videos: length + a still frame, read in the browser before the upload ───
// Best effort: some phones will not draw a frame from a file that has not been
// played. Whatever cannot be read is left out and the upload still goes ahead.
function probeVideo(file) {
  return new Promise((resolve) => {
    const out = { seconds: null, posterBase64: "" };
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(out);
    };
    const timer = setTimeout(finish, 8000);
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.onerror = finish;
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) out.seconds = Math.round(v.duration);
      try { v.currentTime = Math.min(1, (v.duration || 2) / 2); } catch { finish(); }
    };
    v.onseeked = () => {
      try {
        const scale = Math.min(1, 864 / (v.videoWidth || 864));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round((v.videoWidth || 0) * scale));
        canvas.height = Math.max(1, Math.round((v.videoHeight || 0) * scale));
        if (canvas.width > 1 && canvas.height > 1) {
          canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
          out.posterBase64 = canvas.toDataURL("image/jpeg", 0.8).replace(/^data:[^,]*,/, "");
        }
      } catch { /* no poster */ }
      finish();
    };
    v.src = url;
  });
}

// PUT a file to a signed upload link, reporting progress (fetch cannot).
function putWithProgress(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload refused (${xhr.status})`)));
    xhr.onerror = () => reject(new Error("the connection dropped"));
    xhr.send(file);
  });
}

// ── Pictures: read a file, and redraw it smaller only when it is too big ────
const toBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || "").replace(/^data:[^,]*,/, ""));
  r.onerror = () => reject(new Error("read failed"));
  r.readAsDataURL(blob);
});

async function prepareUpload(file) {
  if (PICTURE_TYPES.includes(file.type) && file.size <= MAX_UPLOAD_BYTES) {
    return { contentType: file.type, dataBase64: await toBase64(file) };
  }
  // Width only: a long screenshot must stay readable, so its height is kept in
  // proportion rather than squeezed into a square.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("decode failed"));
      el.src = url;
    });
    const scale = Math.min(1, 1600 / img.naturalWidth, 12000 / img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob || blob.size > MAX_UPLOAD_BYTES) throw new Error("still too large");
    return { contentType: "image/jpeg", dataBase64: await toBase64(blob) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
export default function SopLibrary() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions() || {};
  // In "View As" the owner's effective role is the crew member's, so ask the
  // server for the crew's view too. It can only narrow what comes back.
  const viewParam = isAdmin ? "" : "&view=crew";

  const [articles, setArticles] = useState(null); // null = loading
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [draft, setDraft] = useState(null);       // the procedure being edited, or null
  const [media, setMedia] = useState({ pictures: [], videos: [] }); // signed links for the open procedure

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await apiFetch(`/api/sops?action=list${showArchived ? "&archived=1" : ""}${viewParam}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "The SOP library could not be loaded.");
      setArticles(Array.isArray(data.articles) ? data.articles : []);
      setCanEdit(!!data.canEdit);
    } catch (e) {
      setArticles((prev) => prev || []);
      setError(e.message || "The SOP library could not be loaded.");
    }
  }, [showArchived, viewParam]);

  useEffect(() => { load(); }, [load]);

  const open = useMemo(() => (slug && articles ? articles.find((a) => a.slug === slug) || null : null), [slug, articles]);
  const openId = open?.id || null;
  // WHICH files, not how many: swapping one video for another leaves the count
  // the same, and the page would keep a link to a file that is gone.
  const openMediaKey = [...(open?.images || []), ...(open?.videos || [])].map((x) => x.path).join("|");
  // Bumped to fetch fresh links: they expire after six hours, and a tech who
  // left the page open finds out only when Play does nothing.
  const [mediaTick, setMediaTick] = useState(0);
  const refreshedFor = useRef("");
  // Bumped when a refresh fails. A video element that has failed once reports
  // nothing on the next Play, so it is made again and can ask again.
  const [playerTry, setPlayerTry] = useState(0);

  // A different procedure, or different files on this one: what is on screen
  // belongs to the old one, so clear it, and the one refresh is available again.
  // A refresh of the same files (mediaTick) must NOT clear it: with poor signal
  // the refetch fails too, and the video and the pictures were wiped off the
  // page until the tech left it and came back.
  useEffect(() => {
    setMedia({ pictures: [], videos: [] });
    refreshedFor.current = "";
  }, [openId, openMediaKey]);

  // How many files are on screen now, for the refresh below to compare with.
  const shownFiles = useRef(0);
  useEffect(() => { shownFiles.current = media.pictures.length + media.videos.length; }, [media]);

  // Signed picture and video links for the procedure on screen. What is shown
  // is replaced only by a good answer that carries both lists.
  useEffect(() => {
    let alive = true;
    if (!openId || !openMediaKey) return undefined;
    // Nothing was refreshed, so the one refresh is not used up, and the player
    // is made again: a video element that failed once reports no error on the
    // next Play, so the old one would never ask for new links. This cannot
    // loop: a new player loads nothing until someone presses Play.
    const failed = () => {
      if (!alive) return;
      refreshedFor.current = "";
      setPlayerTry((n) => n + 1);
    };
    apiFetch(`/api/sops?action=get&id=${encodeURIComponent(openId)}${viewParam}`)
      .then(async (r) => ({ ok: !!r.ok, d: await r.json().catch(() => null) }))
      .then(({ ok, d }) => {
        if (!ok || !d || !Array.isArray(d.pictures) || !Array.isArray(d.videos)) { failed(); return; }
        if (!alive) return;
        // A file whose link could not be made is left out of a good answer.
        // Fewer files than are on screen is that, not "the files are gone"
        // (a change of files clears the page first, in the effect above).
        if (shownFiles.current > d.pictures.length + d.videos.length) { failed(); return; }
        setMedia({ pictures: d.pictures, videos: d.videos });
      })
      .catch(failed);
    return () => { alive = false; };
  }, [openId, openMediaKey, viewParam, mediaTick]);

  // A video that will not load most likely has a stale link: get new ones, once
  // per procedure, so a file that is really broken cannot loop. The once is
  // given back when a video shows it can load (onVideoLoaded), so a page left
  // open through a second expiry gets a second refresh.
  const onVideoError = () => {
    if (refreshedFor.current === openId) return;
    refreshedFor.current = openId;
    setMediaTick((n) => n + 1);
  };
  const onVideoLoaded = () => { refreshedFor.current = ""; };

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const sections = useMemo(() => {
    const first = new Map();
    for (const a of articles || []) {
      const at = first.get(a.section);
      if (at === undefined || a.sort_order < at) first.set(a.section, a.sort_order);
    }
    return [...first.entries()].sort((x, y) => x[1] - y[1] || x[0].localeCompare(y[0])).map(([name]) => name);
  }, [articles]);

  const tokens = useMemo(() => tokensOf(query), [query]);
  const results = useMemo(() => {
    if (!articles || !query.trim()) return null;
    return articles
      .map((a) => ({ a, s: score(a, tokens) }))
      .filter((x) => x.s > 0)
      .sort((x, y) => y.s - x.s || x.a.sort_order - y.a.sort_order)
      .map((x) => x.a);
  }, [articles, query, tokens]);

  const startNew = () => setDraft({
    id: null, title: "", section: section !== "all" ? section : sections[0] || "General", summary: "", body: "",
    keywords: [], audience: "crew", status: "draft", sort_order: 100, images: [],
  });

  // ── Editing ──────────────────────────────────────────────────────────────
  if (draft) {
    return (
      <Page>
        <Editor
          draft={draft}
          sections={sections}
          // Pictures and videos are attached the moment they are added, not on
          // Save, so the list is re-read even when the edit is cancelled.
          onCancel={() => { setDraft(null); load(); }}
          onSaved={async (saved, { stay } = {}) => {
            await load();
            if (stay) { setDraft((d) => (d ? { ...d, ...saved, keywords: saved.keywords || [] } : d)); return; }
            setDraft(null);
            if (saved?.slug && saved.status !== "archived") navigate(`/sops/${saved.slug}`);
            else navigate("/sops");
          }}
        />
      </Page>
    );
  }

  // ── One procedure ────────────────────────────────────────────────────────
  if (slug) {
    if (articles === null) return <Page><Muted>Loading…</Muted></Page>;
    if (!open) {
      return (
        <Page>
          <BackLink />
          <div style={{ fontSize: 20, fontWeight: 800, margin: "14px 0 6px" }}>That procedure was not found</div>
          <Muted>It may have been renamed or archived. Search the library for it.</Muted>
        </Page>
      );
    }
    return (
      <Page>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <BackLink />
          {canEdit && <button type="button" style={button()} onClick={() => setDraft({ ...open })}>Edit</button>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.lime, marginTop: 16 }}>{open.section}</div>
        <h1 style={{ fontSize: 27, lineHeight: 1.15, margin: "6px 0 8px", fontWeight: 800, color: "#fff" }}>{open.title}</h1>
        {open.summary && <div style={{ fontSize: 16, color: "#b8c0cf", lineHeight: 1.45 }}>{open.summary}</div>}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "10px 0 2px" }}>
          <StatusBadges a={open} />
          {open.updated_at && <span style={{ fontSize: 12, color: C.faint }}>Updated {fmtDate(open.updated_at)}</span>}
        </div>

        {media.videos.map((v) => (
          <figure key={`${v.path}|${playerTry}`} style={{ margin: "14px 0 6px" }}>
            {/* preload="none": a tech on mobile data pays for the video only if they press play. */}
            <video
              controls
              playsInline
              preload="none"
              poster={v.poster_url || undefined}
              src={v.url}
              onError={onVideoError}
              onLoadedMetadata={onVideoLoaded}
              style={{ display: "block", width: "100%", maxHeight: "74vh", background: "#000", borderRadius: 12, border: `1px solid ${C.border}` }}
            />
            <figcaption style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
              {[v.caption, fmtDuration(v.seconds), fmtSize(v.bytes)].filter(Boolean).join(" · ")}
            </figcaption>
          </figure>
        ))}

        <div style={{ fontSize: 16, color: C.text, marginTop: 8 }}>
          <RichText text={open.body} article accent={C.lime} style={{ lineHeight: 1.55 }} />
        </div>

        {media.pictures.length > 0 && (
          <div style={{ marginTop: 22 }}>
            {media.pictures.map((p) => (
              <figure key={p.path} style={{ margin: "0 0 16px" }}>
                <a href={p.url} target="_blank" rel="noreferrer" title="Open full size">
                  <img src={p.url} alt={p.caption || open.title} style={{ display: "block", width: "100%", borderRadius: 12, border: `1px solid ${C.border}` }} />
                </a>
                {p.caption && <figcaption style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>{p.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}

        <div style={{ marginTop: 26, padding: 14, border: `1px solid ${C.border}`, borderRadius: 14, background: C.surface }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Want it talked through?</div>
          <div style={{ fontSize: 14, color: C.muted, margin: "4px 0 12px", lineHeight: 1.45 }}>Jai-Man reads this same page and can walk you through it one step at a time.</div>
          <button
            type="button"
            style={button("primary")}
            onClick={() => window.dispatchEvent(new CustomEvent("elecbid:ask-jaiman", { detail: { text: `Walk me through the "${open.title}" SOP, one step at a time.` } }))}
          >
            Ask Jai-Man
          </button>
        </div>
      </Page>
    );
  }

  // ── The library ──────────────────────────────────────────────────────────
  const visible = (articles || []).filter((a) => section === "all" || a.section === section);
  const grouped = sections
    .filter((s) => section === "all" || s === section)
    .map((s) => ({ name: s, items: visible.filter((a) => a.section === s).sort((x, y) => x.sort_order - y.sort_order || x.title.localeCompare(y.title)) }))
    .filter((g) => g.items.length);

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 28, margin: 0, fontWeight: 800, color: "#fff" }}>SOPs</h1>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>How we do things. Search it, pick a section, or ask Jai-Man.</div>
        </div>
        {canEdit && <button type="button" style={{ ...button("primary"), flex: "0 0 auto" }} onClick={startNew}>New SOP</button>}
      </div>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search: /stock, return, extra work…"
        aria-label="Search the SOPs"
        style={{ ...input, marginTop: 14 }}
      />

      {error && <div style={{ marginTop: 12, padding: "10px 12px", border: `1px solid ${C.red}`, borderRadius: 10, color: C.red, fontSize: 14 }}>{error}</div>}

      {results ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            {results.length ? `${results.length} match${results.length === 1 ? "" : "es"}` : "Nothing matches that."}
          </div>
          {results.map((a) => <Row key={a.id} a={a} showSection extra={snippet(a, tokens)} />)}
          {!results.length && <Muted>Try one word, like the prompt itself. If it is not written down yet, ask Jai-Man or tell the office so it gets added.</Muted>}
        </div>
      ) : (
        <>
          {sections.length > 1 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "14px 0 4px", WebkitOverflowScrolling: "touch" }}>
              <button type="button" style={chip(section === "all")} onClick={() => setSection("all")}>All</button>
              {sections.map((s) => <button key={s} type="button" style={chip(section === s)} onClick={() => setSection(s)}>{s}</button>)}
            </div>
          )}

          {articles === null && <div style={{ marginTop: 18 }}><Muted>Loading…</Muted></div>}
          {articles && !articles.length && !error && (
            <div style={{ marginTop: 22 }}>
              <Muted>Nothing is written down yet.{canEdit ? " Use New SOP to add the first procedure." : ""}</Muted>
            </div>
          )}

          {grouped.map((g) => (
            <div key={g.name} style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.lime, marginBottom: 8 }}>{g.name}</div>
              {g.items.map((a) => <Row key={a.id} a={a} />)}
            </div>
          ))}

          {canEdit && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24, fontSize: 13, color: C.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived procedures
            </label>
          )}
        </>
      )}
    </Page>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────
function Page({ children }) {
  return <div style={{ maxWidth: 720, margin: "0 auto", padding: "16px 14px 96px", color: C.text }}>{children}</div>;
}

function Muted({ children }) {
  return <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>{children}</div>;
}

function BackLink() {
  return <Link to="/sops" style={{ color: C.lime, textDecoration: "none", fontSize: 15, fontWeight: 700 }}>‹ All SOPs</Link>;
}

function StatusBadges({ a }) {
  return (
    <>
      {a.status === "draft" && <span style={badge(C.yellow)}>Draft</span>}
      {a.status === "archived" && <span style={badge(C.red)}>Archived</span>}
      {a.audience === "office" && <span style={badge(C.muted)}>Office only</span>}
    </>
  );
}

function Row({ a, showSection = false, extra = "" }) {
  return (
    <Link
      to={`/sops/${a.slug}`}
      style={{ display: "block", textDecoration: "none", color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 14px", marginBottom: 10, opacity: a.status === "archived" ? 0.6 : 1 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", lineHeight: 1.25 }}>{a.title}</div>
        <span style={{ color: C.faint, fontSize: 20, flex: "0 0 auto" }}>›</span>
      </div>
      {a.summary && <div style={{ fontSize: 14, color: "#b8c0cf", marginTop: 4, lineHeight: 1.4 }}>{a.summary}</div>}
      {extra && <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>{extra}</div>}
      {(showSection || a.status !== "published" || a.audience === "office" || (a.images || []).length > 0 || (a.videos || []).length > 0) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          {showSection && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.lime }}>{a.section}</span>}
          <StatusBadges a={a} />
          {(a.videos || []).length > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: C.lime }}>
              ▶ Video{a.videos.length > 1 ? `s (${a.videos.length})` : ""}{a.videos.length === 1 && fmtDuration(a.videos[0].seconds) ? ` ${fmtDuration(a.videos[0].seconds)}` : ""}
            </span>
          )}
          {(a.images || []).length > 0 && <span style={{ fontSize: 12, color: C.faint }}>{a.images.length === 1 ? "1 picture" : `${a.images.length} pictures`}</span>}
        </div>
      )}
    </Link>
  );
}

// ── Editor (admin) ────────────────────────────────────────────────────────────
function Editor({ draft, sections, onCancel, onSaved }) {
  const [f, setF] = useState({ ...draft, keywordsText: (draft.keywords || []).join(", ") });
  const [pictures, setPictures] = useState([]);
  const [videos, setVideos] = useState([]);
  const [progress, setProgress] = useState(0);   // 0..1 while a video is going up
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const isNew = !f.id;

  // Signed links for the pictures and videos already attached.
  useEffect(() => {
    let alive = true;
    if (!draft.id || !((draft.images || []).length + (draft.videos || []).length)) return undefined;
    apiFetch(`/api/sops?action=get&id=${encodeURIComponent(draft.id)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setPictures(Array.isArray(d.pictures) ? d.pictures : []);
        setVideos(Array.isArray(d.videos) ? d.videos : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [draft.id, draft.images, draft.videos]);

  const post = async (payload) => {
    const res = await apiFetch("/api/sops", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "That did not save. Try again.");
    return data;
  };

  const fields = (over = {}) => ({
    action: "save", ...(f.id ? { id: f.id } : {}),
    title: f.title, section: f.section, summary: f.summary || "", body: f.body || "",
    keywords: f.keywordsText, audience: f.audience, status: f.status, sort_order: f.sort_order, ...over,
  });

  const save = async (over) => {
    setError(""); setBusy("save");
    try {
      const data = await post(fields(over));
      await onSaved(data.article);
    } catch (e) { setError(e.message); setBusy(""); }
  };

  // A new procedure needs a row before a picture or a video can hang off it.
  const ensureSaved = async (what) => {
    if (f.id) return f.id;
    if (!String(f.title || "").trim()) throw new Error(`Give it a title first, then add the ${what}.`);
    const created = await post(fields());
    setF((p) => ({ ...p, id: created.article.id, slug: created.article.slug }));
    await onSaved(created.article, { stay: true });
    return created.article.id;
  };

  const addVideo = async (file) => {
    if (!file) return;
    setError(""); setBusy("video"); setProgress(0);
    try {
      if (!VIDEO_TYPES.includes(file.type)) throw new Error("That file is not a video ElecBid can play. Use an MP4 (a phone screen recording is one).");
      if (file.size > MAX_VIDEO_BYTES) throw new Error("That video is over 500 MB. Trim it or record a shorter one.");
      const id = await ensureSaved("video");
      const [link, probe] = await Promise.all([
        post({ action: "video-upload-url", id, contentType: file.type, bytes: file.size }),
        probeVideo(file),
      ]);
      try { await putWithProgress(link.uploadUrl, file, link.contentType, setProgress); }
      catch (e) { throw new Error(`The video did not upload: ${e.message}. Check your connection and try again.`); }
      const data = await post({ action: "add-video", id, path: link.path, seconds: probe.seconds, posterBase64: probe.posterBase64 });
      setVideos(data.videos || []);
      setF((p) => ({ ...p, videos: data.article?.videos || p.videos }));
    } catch (e) { setError(e.message); }
    setBusy(""); setProgress(0);
    if (videoRef.current) videoRef.current.value = "";
  };

  const removeVideo = async (path) => {
    setError(""); setBusy("video");
    try {
      const data = await post({ action: "remove-video", id: f.id, path });
      setVideos(data.videos || []);
      setF((p) => ({ ...p, videos: data.article?.videos || [] }));
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const addPicture = async (file) => {
    if (!file) return;
    setError(""); setBusy("picture");
    try {
      const id = await ensureSaved("picture");
      let upload;
      try { upload = await prepareUpload(file); }
      catch { throw new Error("That picture could not be read. Use a PNG or JPEG under 3 MB."); }
      const data = await post({ action: "add-image", id, ...upload });
      setPictures(data.pictures || []);
      setF((p) => ({ ...p, images: data.article?.images || p.images }));
    } catch (e) { setError(e.message); }
    setBusy("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePicture = async (path) => {
    setError(""); setBusy("picture");
    try {
      const data = await post({ action: "remove-image", id: f.id, path });
      setPictures(data.pictures || []);
      setF((p) => ({ ...p, images: data.article?.images || [] }));
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <h1 style={{ fontSize: 24, margin: 0, fontWeight: 800, color: "#fff" }}>{isNew ? "New SOP" : "Edit SOP"}</h1>
        <button type="button" style={button()} onClick={onCancel} disabled={!!busy}>Cancel</button>
      </div>

      <label style={label} htmlFor="sop-title">Title</label>
      <input id="sop-title" style={input} value={f.title || ""} onChange={set("title")} placeholder="/stock: used it off the van" maxLength={140} />

      <label style={label} htmlFor="sop-section">Section</label>
      <input id="sop-section" style={input} value={f.section || ""} onChange={set("section")} list="sop-sections" placeholder="Jobber notes" maxLength={60} />
      <datalist id="sop-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>

      <label style={label} htmlFor="sop-summary">One line under the title</label>
      <input id="sop-summary" style={input} value={f.summary || ""} onChange={set("summary")} placeholder="What it is for, in a sentence" maxLength={240} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
        <label style={{ ...label, margin: 0 }} htmlFor="sop-body">The procedure</label>
        <button type="button" style={{ ...chip(preview), padding: "5px 11px" }} onClick={() => setPreview((v) => !v)}>{preview ? "Back to writing" : "Preview"}</button>
      </div>
      {preview ? (
        <div style={{ marginTop: 6, padding: 14, border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, fontSize: 16, minHeight: 160 }}>
          <RichText text={f.body || "Nothing written yet."} article accent={C.lime} style={{ lineHeight: 1.55 }} />
        </div>
      ) : (
        <>
          <textarea id="sop-body" style={{ ...input, marginTop: 6, minHeight: 300, fontFamily: "ui-monospace, Consolas, monospace", fontSize: 15, lineHeight: 1.5, resize: "vertical" }} value={f.body || ""} onChange={set("body")} />
          <div style={{ fontSize: 12, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
            Start a line with <b>#</b> for a heading, <b>1.</b> for a step, <b>-</b> for a bullet. Put <b>```</b> on the line above and below anything typed exactly as shown. It gets a Copy button. For an example that is only there to be read, write <b>```read</b> on the line above instead. That box has no Copy button.
          </div>
        </>
      )}

      <label style={label} htmlFor="sop-keywords">Search words</label>
      <input id="sop-keywords" style={input} value={f.keywordsText || ""} onChange={set("keywordsText")} placeholder="/stock, van stock, used" />
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>Other words someone might type to find this, separated by commas.</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={label} htmlFor="sop-audience">Who can read it</label>
          <select id="sop-audience" style={input} value={f.audience} onChange={set("audience")}>
            <option value="crew">Everyone</option>
            <option value="office">Office only</option>
          </select>
        </div>
        <div>
          <label style={label} htmlFor="sop-status">Status</label>
          <select id="sop-status" style={input} value={f.status} onChange={set("status")}>
            <option value="draft">Draft (admins only)</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <label style={label} htmlFor="sop-order">Order in its section</label>
      <input id="sop-order" style={{ ...input, maxWidth: 140 }} type="number" min="0" max="9999" value={f.sort_order ?? 100} onChange={set("sort_order")} />
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>Lower numbers come first.</div>

      <label style={label}>Videos</label>
      {videos.map((v) => (
        <div key={v.path} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
          {v.poster_url
            ? <img src={v.poster_url} alt="" style={{ width: 64, height: 64, objectFit: "cover", objectPosition: "top", borderRadius: 8, border: `1px solid ${C.border}` }} />
            : <div style={{ width: 64, height: 64, borderRadius: 8, border: `1px solid ${C.border}`, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", color: C.lime, fontSize: 20 }}>▶</div>}
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: C.muted }}>{[fmtDuration(v.seconds), fmtSize(v.bytes)].filter(Boolean).join(" · ") || "Video"}</div>
          <button type="button" style={button("danger")} onClick={() => removeVideo(v.path)} disabled={!!busy}>Remove</button>
        </div>
      ))}
      <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" style={{ display: "none" }} onChange={(e) => addVideo(e.target.files?.[0])} />
      <button type="button" style={button()} onClick={() => videoRef.current?.click()} disabled={!!busy}>
        {busy === "video" ? (progress > 0 && progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : "Working…") : "Add a video"}
      </button>
      <div style={{ fontSize: 12, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>A phone screen recording works as it is. Use Wi-Fi: a few minutes of recording is 50 to 200 MB. Keep this page open until it finishes.</div>

      <label style={label}>Pictures</label>
      {pictures.map((p) => (
        <div key={p.path} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
          <img src={p.url} alt="" style={{ width: 64, height: 64, objectFit: "cover", objectPosition: "top", borderRadius: 8, border: `1px solid ${C.border}` }} />
          <button type="button" style={button("danger")} onClick={() => removePicture(p.path)} disabled={!!busy}>Remove</button>
        </div>
      ))}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => addPicture(e.target.files?.[0])} />
      <button type="button" style={button()} onClick={() => fileRef.current?.click()} disabled={!!busy}>{busy === "picture" ? "Working…" : "Add a picture"}</button>

      {error && <div style={{ marginTop: 16, padding: "10px 12px", border: `1px solid ${C.red}`, borderRadius: 10, color: C.red, fontSize: 14 }}>{error}</div>}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 22 }}>
        <button type="button" style={button("primary")} onClick={() => save()} disabled={!!busy}>{busy === "save" ? "Saving…" : "Save"}</button>
        {f.status === "draft" && <button type="button" style={button()} onClick={() => save({ status: "published" })} disabled={!!busy}>Save and publish</button>}
      </div>
    </div>
  );
}
